import classNames from 'classnames'
import CustomBottomSheet from 'components/bottomSheet'
import CustomDropDown from 'components/dropDown'
import ChangeShareModal from 'components/modals/changeShare'
import { Formik, Form } from 'formik'
import { useEffect, useState } from 'react'
import { NumericFormat } from 'react-number-format'
import { useNavigate, useParams } from 'react-router-dom'
import FormikInput from 'components/formikFields/input'
import Switch from 'react-switch'
import useUpdatePortion from '../../hooks/useUpdatePortion'
import DetactivateModal from 'components/modals/deactivate'
import { FiPhone } from 'react-icons/fi'
import { MdPayment } from 'react-icons/md'

interface ItemProps {
    item: IMoneyPoolResults
    shareValueAmount: number
    setIsOpenPayToOtherModal: React.Dispatch<React.SetStateAction<boolean>>
    setSelectUser: React.Dispatch<
        React.SetStateAction<IMoneyPoolResults | undefined>
    >
    isManager: boolean
    refetch: () => void
}
export default function Item({
    item,
    shareValueAmount,
    setIsOpenPayToOtherModal,
    setSelectUser,
    isManager,
    refetch,
}: ItemProps) {
    const navigate = useNavigate()
    const [isOpenShereModal, setIsOpenShareModal] = useState<boolean>(false)
    const [isOpenChangePrice, setIsOpenChangePrice] = useState<boolean>(false)
    const [isPend, setIsPend] = useState<boolean>(true)
    const [isDeactive, setIsDeactive] = useState<boolean>(false)

    const { mutate } = useUpdatePortion(refetch)
    const { id } = useParams()
    const [isMe, setIsMe] = useState<boolean>(false)

    const profile = JSON.parse(localStorage.getItem('profile')!)
    useEffect(() => {
        if (profile) {
            if (profile.id === item.member.id) {
                setIsMe(true)
            }
        }
    }, [profile])

    const isDelayed = item.portion_payment_status === 'ps_delayed'
    const isUnpaid = item.portion_payment_status === 'ps_unpaid'
    const isPaid =
        item.portion_payment_status === 'ps_paid' ||
        item.portion_payment_status === 'ps_overpaid'

    // Android: status/pay are only meaningful while remainingAmount() > 0
    // (remainingAmount maps to unpaid_portion) and totalToBePaid != 0
    // (totalToBePaid maps to to_be_paid_portion). (adapter lines 568-604)
    const remaining = item.unpaid_portion ?? 0
    const totalToBePaid = item.to_be_paid_portion ?? 0
    const hasRemaining = remaining > 0 && totalToBePaid !== 0

    // status badge visibility, gated like the adapter
    const showPaidBadge = isPaid && hasRemaining
    const showUnpaidBadge = isUnpaid && hasRemaining
    const showDelayedBadge = isDelayed && hasRemaining

    // pay button: only for unpaid/delayed while remaining, and never for
    // hybrid single-payer non-managers (adapter lines 582,584,598-600).
    const showPayBtn = (isUnpaid || isDelayed) && hasRemaining

    // pay_view fill band: paid fraction of the card width (adapter 626-644)
    let payPercent = totalToBePaid === 0 ? 1 : item.paid_portion / totalToBePaid
    if (remaining === 0 && item.number_of_shares === 0) payPercent = 1
    payPercent = Math.max(0, Math.min(1, payPercent))

    return (
        <>
            {item && (
                <div
                    className="mx-4 mb-1.5 min-h-[75px] rounded-[4px] border border-[#EFEFEF] bg-white relative cursor-pointer active:opacity-90"
                    onClick={() =>
                        navigate(
                            `/fund/financial-history/${item.moneypool}/${item.id}`,
                            { state: { shareValueAmount } }
                        )
                    }
                >
                    {/* pay_view: translucent-green fill band of the paid
                        fraction, anchored to the end (right in RTL) */}
                    <div
                        className="absolute inset-y-0 right-0 rounded-r-[4px] bg-[#74B74377] pointer-events-none"
                        style={{ width: `${payPercent * 100}%` }}
                    />

                    {/* flex: name+badge (right) | icons+pay (left) — no overlap.
                        NOTE: keep this positioned but WITHOUT z-index — a z-index
                        here creates a stacking context that traps the dropdown
                        below later sibling cards. */}
                    <div className="relative flex items-start justify-between gap-2 p-2.5">
                        {/* RIGHT (RTL first): name row + status badge */}
                        <div className="flex min-w-0 flex-col gap-1.5">
                            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                                <p className="text-sm font-bold text-black">
                                    {item?.member?.first_name}{' '}
                                    {item?.member?.last_name}
                                </p>
                                <span className="text-sm text-black">
                                    - {item.number_of_shares} سهم
                                </span>
                                {item.role === 'own' && (
                                    <span className="bg-[#EFEFEF] text-black rounded-[4px] px-[5px] text-sm font-bold">
                                        ایجادکننده
                                    </span>
                                )}
                                {item.role === 'mgr' && (
                                    <span className="bg-[#EFEFEF] text-black rounded-[4px] px-[5px] text-sm font-bold">
                                        مدیر
                                    </span>
                                )}
                            </div>
                            {showPaidBadge && (
                                <span className="w-fit bg-green-1 text-white rounded-[4px] px-1.5 py-0.5 text-xs">
                                    پرداخت شده
                                </span>
                            )}
                            {showUnpaidBadge && (
                                <span className="w-fit bg-[#EFEFEF] text-black rounded-[4px] px-1.5 py-0.5 text-xs">
                                    پرداخت نشده:{' '}
                                    {item.to_be_paid_portion &&
                                        item.to_be_paid_portion.toLocaleString()}{' '}
                                    تومان
                                </span>
                            )}
                            {showDelayedBadge && (
                                <span className="w-fit bg-[#E41F1F] text-white rounded-[4px] px-1.5 py-0.5 text-xs">
                                    تأخیر در پرداخت:{' '}
                                    {item.unpaid_portion &&
                                        item.unpaid_portion.toLocaleString()}{' '}
                                    تومان
                                </span>
                            )}
                        </div>

                        {/* LEFT (RTL last): icons row + pay button */}
                        <div className="flex shrink-0 flex-col items-center gap-1.5">
                            <div className="flex items-center gap-2">
                                {item.member?.phone_number && (
                                    <a
                                        href={`tel:${item.member.phone_number}`}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <FiPhone className="w-5 h-6 text-black" />
                                    </a>
                                )}
                                <div
                                    className="shrink-0 [&_img]:w-[23px] [&_img]:h-[25px]"
                                    onClick={(event) => {
                                        event.stopPropagation()
                                    }}
                                >
                                    {/* Android PopupBalloonMenu: light-blue
                                        #E3EFF4 balloon ABOVE the dots, 10dp
                                        radius, uniform black 16sp items with
                                        dividers, no icons */}
                                    <CustomDropDown
                                        align="top"
                                        arrowColor="#E3EFF4"
                                        listWrapperClassName="min-w-[188px] rounded-[10px] bg-[#E3EFF4] shadow-[0_4px_20px_rgba(0,0,0,0.18)]"
                                    >
                                        <div className="divide-y divide-[#D4D4D4] text-base text-black text-right">
                                            <p
                                                className="flex min-h-[50px] cursor-pointer items-center py-2 pr-5 pl-12"
                                                onClick={() =>
                                                    navigate(
                                                        `/fund/financial-history/${item.moneypool}/${item.id}`,
                                                        {
                                                            state: {
                                                                shareValueAmount,
                                                            },
                                                        }
                                                    )
                                                }
                                            >
                                                اطلاعات بیشتر
                                            </p>
                                            {isManager && (
                                                <>
                                                    <p
                                                        className="flex min-h-[50px] cursor-pointer items-center py-2 pr-5 pl-12"
                                                        onClick={() =>
                                                            setIsOpenShareModal(
                                                                true
                                                            )
                                                        }
                                                    >
                                                        تغییر تعداد سهم
                                                    </p>
                                                    <p
                                                        className="flex min-h-[50px] cursor-pointer items-center py-2 pr-5 pl-12"
                                                        onClick={() =>
                                                            setIsOpenChangePrice(
                                                                true
                                                            )
                                                        }
                                                    >
                                                        تغییر مبلغ تعهدات
                                                    </p>
                                                    {!isMe && (
                                                        <p
                                                            className="flex min-h-[50px] cursor-pointer items-center py-2 pr-5 pl-12"
                                                            onClick={() =>
                                                                setIsDeactive(
                                                                    true
                                                                )
                                                            }
                                                        >
                                                            غیرفعال سازی
                                                        </p>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </CustomDropDown>
                                </div>
                            </div>
                            {showPayBtn && (
                                <button
                                    className="flex items-center gap-1 rounded-[4px] bg-green-1 px-2.5 h-7 text-xs font-bold text-white whitespace-nowrap"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        setIsOpenPayToOtherModal(true)
                                        setSelectUser(item)
                                    }}
                                >
                                    <MdPayment className="w-5 h-5" />
                                    پرداخت
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <ChangeShareModal
                show={isOpenShereModal}
                onHide={() => setIsOpenShareModal(false)}
                user={item}
            />

            <CustomBottomSheet
                isOpen={isOpenChangePrice}
                onDismiss={() => setIsOpenChangePrice(false)}
            >
                <>
                    <p className=" text-center text-gray-1">
                        تغییر مبلغ تعهدات
                    </p>
                    <hr className=" my-2" />
                    <Formik
                        initialValues={{
                            amount: item.to_be_paid_portion
                                ? item.to_be_paid_portion
                                : '',
                        }}
                        onSubmit={async (values) => {
                            const payload: {
                                moneyPool: String | undefined
                                poolship: number
                                amount: number
                                is_delayed: boolean
                            } = {
                                moneyPool: id,
                                poolship: item.id,
                                amount: Number(values.amount),
                                is_delayed: isPend,
                            }
                            mutate(payload)
                            setIsOpenChangePrice(false)
                        }}
                        enableReinitialize
                    >
                        {({ values, touched, errors, setFieldValue }) => (
                            <Form className=" mx-4 my-2">
                                <NumericFormat
                                    customInput={FormikInput}
                                    thousandSeparator
                                    allowNegative={false}
                                    label=""
                                    name="amount"
                                    placeholder="مبلغ قابل پرداخت (تومان)"
                                    className="text-sm font-normal my-2"
                                    defaultValue={values.amount}
                                    onValueChange={(values) => {
                                        const { value } = values
                                        setFieldValue('amount', value)
                                    }}
                                />
                                <div className=" w-full flex my-3 items-center">
                                    <Switch
                                        onChange={() => setIsPend(!isPend)}
                                        checked={isPend}
                                        onColor="#75B844"
                                        onHandleColor="#EAEEF0"
                                        handleDiameter={25}
                                        uncheckedIcon={false}
                                        checkedIcon={false}
                                        boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                                        activeBoxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                                        height={20}
                                        width={50}
                                        className="react-switch"
                                        id="material-switch"
                                        offColor="#B6BCC8"
                                    />
                                    <p className=" text-sm mx-2 text-gray-1">
                                        تاخیر در پرداخت
                                    </p>
                                </div>
                                <div className=" w-full flex justify-between">
                                    <button
                                        type="submit"
                                        className=" text-white bg-green-1 rounded-md w-full ml-1 py-2"
                                    >
                                        تایید
                                    </button>
                                    <button
                                        onClick={() =>
                                            setIsOpenChangePrice(false)
                                        }
                                        className=" text-green-1 border-1 border-green-1 rounded-md w-full py-2 mr-1"
                                    >
                                        انصراف
                                    </button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </>
            </CustomBottomSheet>
            <DetactivateModal
                show={isDeactive}
                onHide={() => setIsDeactive(false)}
                user={item}
                refetch={refetch}
            />
        </>
    )
}
